import React, {useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'next-i18next';
import {useLocalizedFormat} from '@deezer/next/contexts/localization';
import {Box} from '@tempo/core';
import type {EChartsReactRef} from '@components/EChartsReact';
import ReactECharts from '@components/EChartsReact';
import {getDateFormatFromValue} from '@components/EChartsReact/utils';
import type {StreamData} from '@deezer/next/services/api/Legacy/requests/analytic/types';
import * as echartsLib from 'echarts';
import {toUTCTimestamp} from '@deezer/next/shared/utils/date';

type EChartsTimeSeriesProps = {
	data: StreamData;
	isLoading?: boolean;
};

const DAY_IN_MS = 3600 * 24 * 1000;

const normalizeSeriesData = (
	series: StreamData['nbStreams'],
): StreamData['nbStreams'] =>
	series.map(point => {
		if (!Array.isArray(point)) {
			return point;
		}

		const [date, ...values] = point;

		return [toUTCTimestamp(date), ...values] as typeof point;
	});

const TimeSeries = ({data, isLoading}: EChartsTimeSeriesProps) => {
	const {t} = useTranslation('d4c');
	const {formatDate, formatNumber} = useLocalizedFormat();
	const echartsRef = useRef<EChartsReactRef>(null);

	const nbStreams = useMemo(
		() => normalizeSeriesData(data.nbStreams),
		[data.nbStreams],
	);

	const nbListeners = useMemo(
		() => normalizeSeriesData(data.nbListeners),
		[data.nbListeners],
	);

	const options = useMemo(() => {
		return {
			legend: {
				left: 0,
			},

			grid: {
				bottom: 80,
				left: 0,
				right: 0,
				top: 60,
				outerBoundsMode: 'none' as const,
			},

			useUTC: true,

			tooltip: {
				trigger: 'axis',

				valueFormatter: (value: number) => formatNumber(value),

				axisPointer: {
					label: {
						formatter: ({value}: {value: number}) =>
							formatDate(value, 'P', {
								timeZone: 'UTC',
							}),
					},
				},
			},

			xAxis: {
				type: 'time',
				alignTicks: true,

				axisLine: {
					show: false,
				},

				axisTick: {
					show: false,
				},

				axisLabel: {
					showMinLabel: false,
					showMaxLabel: false,
					hideOverlap: true,

					formatter: (value: number) =>
						formatDate(value, getDateFormatFromValue(value, true), {
							timeZone: 'UTC',
						}),
				},
			},

			yAxis: [
				{
					type: 'value',
					show: false,
					min: 'dataMin',
				},
				{
					type: 'value',
					show: false,
					min: 'dataMin',
				},
			],

			dataZoom: [
				{
					type: 'inside',
					xAxisIndex: 0,
					minValueSpan: DAY_IN_MS * 5,
					filterMode: 'continuityFilter',
					start: 0,
					end: 100,
				},
				{
					type: 'slider',
					xAxisIndex: 0,
					minValueSpan: DAY_IN_MS * 5,
					filterMode: 'continuityFilter',

					labelFormatter: (value: number) => {
						if (isNaN(value)) {
							return 0;
						}

						try {
							return formatDate(value, 'P HH:mm', {
								timeZone: 'UTC',
							});
						} catch {
							return value;
						}
					},

					start: 0,
					end: 100,
				},
			],

			series: [
				{
					name: t('analyticshome_text_streams_d4c-web'),
					type: 'line',
					smooth: true,
					symbol: nbStreams.length === 1 ? 'circle' : 'none',
					symbolSize: 10,
					data: nbStreams,
					yAxisIndex: 0,

					areaStyle: {
						color: new echartsLib.graphic.LinearGradient(0, 0, 0, 1, [
							{
								offset: 0,
								color: 'rgba(162, 56, 255, 0.5)',
							},
							{
								offset: 1,
								color: 'rgba(255, 255, 255, 0)',
							},
						]),
					},
				},
				{
					name: t('analyticshome_text_uniquelisteners_d4c-web'),
					type: 'line',
					smooth: true,
					symbol: nbListeners.length === 1 ? 'circle' : 'none',
					symbolSize: 10,
					data: nbListeners,
					yAxisIndex: 1,

					areaStyle: {
						color: new echartsLib.graphic.LinearGradient(0, 0, 0, 1, [
							{
								offset: 0,
								color: 'rgba(218, 176, 255, 0.5)',
							},
							{
								offset: 1,
								color: 'rgba(255, 255, 255, 0)',
							},
						]),
					},
				},
			],
		};
	}, [formatDate, formatNumber, nbListeners, nbStreams, t]);

	// TODO improve by using new proper loading animation
	useEffect(() => {
		const echarts = echartsRef.current?.getEchartsInstance();

		if (!echarts) {
			return;
		}

		if (isLoading) {
			echarts.showLoading({
				text: '',
				maskColor: 'rgba(0, 0, 0, 0.2)',
				spinnerRadius: 16,
				fontSize: 16,
			});
		}

		return () => {
			echarts.hideLoading();
		};
	}, [isLoading]);

	return (
		<Box mt={6}>
			<ReactECharts
				ref={echartsRef}
				option={options}
				opts={{renderer: 'svg'}}
				style={{height: 340}}
			/>
		</Box>
	);
};

export default TimeSeries;
